import {
  createUserCalendarEvent,
  saveUserCalendarEvents,
} from "@/components/daily/companionCalendarV2";

const pad = (value) => String(value).padStart(2, "0");
const localDateKey = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const localTimeKey = (date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;

const cleanTitle = (value) =>
  String(value || "APOS workout")
    .replace(/^use\s+/i, "")
    .replace(/\s+as today['’]s physical outlet$/i, "")
    .trim();

const defaultStart = ({ now, anchorTime }) => {
  const current = new Date(now);
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(anchorTime || ""));

  if (match) {
    const candidate = new Date(current);
    candidate.setHours(Number(match[1]), Number(match[2]), 0, 0);
    if (candidate.getTime() <= current.getTime() + 15 * 60 * 1000) {
      candidate.setDate(candidate.getDate() + 1);
    }
    return candidate;
  }

  const candidate = new Date(current.getTime() + 60 * 60 * 1000);
  const roundedMinutes = Math.ceil(candidate.getMinutes() / 15) * 15;
  candidate.setMinutes(roundedMinutes, 0, 0);
  return candidate;
};

const inferModality = (plan) => {
  const text = [
    plan?.immediate?.activityName,
    plan?.immediate?.title,
    plan?.immediate?.instruction,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (/strength|resistance|weight|squat|hinge|push|pull|carry|machine|dumbbell|barbell/.test(text)) {
    return "strength";
  }
  if (/walk|run|cycle|row|swim|hill|stairs|aerobic|cardio|interval|treadmill/.test(text)) {
    return "cardio";
  }
  return "mixed";
};

const inferCategory = (plan) => {
  const outputClass = plan?.immediate?.outputClass;
  if (["selected-activity", "similar-activity"].includes(outputClass)) {
    return "recreation";
  }
  return "workout";
};

const inferUsualEffort = (plan) => {
  const outputClass = plan?.immediate?.outputClass;
  if (outputClass === "high-output") return "challenging";
  if (outputClass === "low-activation") return "easy";
  return "moderate";
};

const workoutEligible = (plan) =>
  Boolean(
    plan?.immediate &&
      plan?.readiness?.level !== "pause" &&
      !["somatic", "minimum"].includes(plan.immediate.outputClass),
  );

export function buildCompanionWorkoutDraft({
  plan,
  checkIn,
  now = new Date(),
  sourceId,
} = {}) {
  if (!workoutEligible(plan)) return null;

  const start = defaultStart({ now, anchorTime: plan?.anchor?.time });
  const mainMinutes = Math.max(
    1,
    Number(plan.immediate.durationMinutes || plan.immediate.duration) || 15,
  );
  const cooldownMinutes = Math.max(0, Number(plan.cooldown?.duration) || 0);
  const durationMinutes = Math.max(5, mainMinutes + cooldownMinutes);
  const preparation = (plan.preparation?.steps || []).filter(Boolean);
  const title = cleanTitle(plan.immediate.activityName || plan.immediate.title);
  const state = plan.currentState?.system || "Current state";
  const target = plan.stateInterpretation?.targetExplanation || "Use a matched physical output and complete recovery.";
  const main = plan.immediate.instruction;
  const cooldown = plan.cooldown?.instruction || "Reduce the pace gradually and observe the recovery response.";
  const description = [
    `Current state: ${state}`,
    `Goal: ${target}`,
    preparation.length ? `Preparation:\n${preparation.map((step) => `- ${step}`).join("\n")}` : null,
    `Main output (${mainMinutes} min):\n${main}`,
    plan.immediate.intensity ? `Relative intensity: ${plan.immediate.intensity}` : null,
    cooldownMinutes ? `Cooldown (${cooldownMinutes} min):\n${cooldown}` : `Finish:\n${cooldown}`,
    plan.immediate.reassess || "Afterward, notice whether tension, urgency, energy, attention, or the behavioural urge changed.",
  ]
    .filter(Boolean)
    .join("\n\n");

  return {
    id: `companion-workout-${sourceId || Date.now()}`,
    title: `APOS · ${title}`,
    date: localDateKey(start),
    time: localTimeKey(start),
    durationMinutes,
    category: inferCategory(plan),
    modality: inferModality(plan),
    usualEffort: inferUsualEffort(plan),
    repeat: "none",
    weekdays: [],
    reminderMinutes: 30,
    description,
    activityKey: plan.immediate.activityKey || null,
    segments: {
      preparation,
      main,
      mainMinutes,
      intensity: plan.immediate.intensity || null,
      cooldown,
      cooldownMinutes,
    },
    state: {
      system: state,
      labels: plan.currentState?.labels || [],
      energy: Number(checkIn?.energy) || null,
      readiness: checkIn?.physicalReadiness || null,
    },
  };
}

export function addCompanionWorkoutDraft({ draft, events = [] }) {
  if (!draft) return { event: null, events, added: false };
  const existing = events.find((event) => event.id === draft.id);
  if (existing) return { event: existing, events, added: false };

  const event = createUserCalendarEvent(draft);
  const saved = saveUserCalendarEvents([...events, event]);
  return { event, events: saved, added: true };
}

export function formatCompanionWorkoutDateTime(draft) {
  if (!draft?.date) return "Time not set";
  const date = new Date(`${draft.date}T${draft.time || "12:00"}:00`);
  if (Number.isNaN(date.getTime())) return `${draft.date} · ${draft.time || "Time not set"}`;
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}
